import React from 'react';

import dynamic from 'next/dynamic';

import Box from '@mui/material/Box';
import MuiSlider from '@mui/material/Slider';

import { breakpoints } from 'src/constants/breakpoints';
import { colours } from 'src/constants/colours';
import { defaultAudioImagePath } from 'src/constants/defaultImages';
import { timeSliderSteps, volumeSliderSteps } from 'src/contexts/AudioProvider';
import { styledComponent, styledTag } from 'src/utils/styled';

const PlayIcon = dynamic(() => import('src/assets/icons/play.svg'), { ssr: false });
const PauseIcon = dynamic(() => import('src/assets/icons/pause.svg'), { ssr: false });
const VolumeOnIcon = dynamic(() => import('src/assets/icons/volume-on.svg'), { ssr: false });
const VolumeOffIcon = dynamic(() => import('src/assets/icons/volume-off.svg'), { ssr: false });

type styledProps = {
    thumbnailUrl?: string;
};

const formatSeconds = (secondsToFormat: number): string => {
    if (secondsToFormat > 3600) {
        return new Date(secondsToFormat * 1000).toISOString().slice(11, 19);
    } else {
        return new Date(secondsToFormat * 1000).toISOString().slice(14, 19);
    }
};

const StyledAudioContainer = styledComponent(Box)<styledProps>`
    background-image: url(${(props) => (props.thumbnailUrl ? props.thumbnailUrl : defaultAudioImagePath)});
    background-color: ${colours.grey.G50};
    background-size: cover;
    background-position: center;
    display: flex;
    align-items: flex-end;
    width: 100%;
`;

const StyledAudioControlsContainer = styledComponent(Box)`
    width: 100%;
    padding: 0 0.75rem 2.5rem;
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
    z-index: 1;
    @media (max-width: ${breakpoints.tabletPortrait}px) {
        padding: 0 0.75rem 1.5rem;
    }
`;

const StyledPlayPauseBtnContainer = styledTag('button')`
    background: transparent;
    border: 0;
    cursor: pointer;
    margin-right: 0.65rem;
    &:hover svg {
        filter: drop-shadow(0 0 0.65rem ${colours.grey.main}BF);
    }
`;

const StyledTimelineContainer = styledComponent(Box)`
    width: 100%;
    .MuiSlider-root {
        margin-top: 0.125rem;
        color: ${colours.grey.G50};
    }
`;

const StyledVolumeContainer = styledComponent(Box)`
    position: relative;
    padding: 0 0.3rem 0 0.65rem;
    button {
        background: transparent;
        cursor: pointer;
        border: 0;
        padding: 0.65rem;
        &:hover svg {
            filter: drop-shadow(0 0 0.65rem ${colours.grey.main}BF);
        }
    }
    .MuiSlider-root {
        position: absolute;
        left: calc(50% - 0.8rem);
        bottom: 3rem;
        height: 0;
        opacity: 0;
        color: ${colours.grey.G50};
        transition: height 0.3s ease-in-out, opacity 0.3s ease-in-out;
        padding-bottom: 0.65rem;
    }
    &:hover .MuiSlider-root {
        height: 5rem;
        opacity: 1;
    }
`;

const StyledCurrentTimeContainer = styledComponent(Box)`
    user-select: none;
    padding: 0.3rem 0.65rem;
    font-size: 0.875rem;
    color: ${colours.grey.G50};
    background-color: ${colours.grey.G800}CC;
    border-radius: 0.25rem;
    margin: 0 0.65rem 0.3rem 0;
`;

const StyledControlsOverlay = styledComponent(Box)`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    background: linear-gradient(to bottom, transparent 60%, #132e4859);
`;

interface Props {
    thumbnailUrl?: string;
    onPlayPause: () => Promise<unknown>;
    playing: boolean;
    onTimeSliderChangeCommitted: (value: number) => unknown;
    onTimeSliderChange: (value: number) => unknown;
    timeSliderValue: number;
    onVolumeChange: (value: number) => unknown;
    volume: number;
    onMute: () => unknown;
    isMuted: boolean;
    currentTime: number;
    totalDuration: number;
    disabledControls?: boolean;
}

const AudioPlayer: React.FC<Props> = ({
    thumbnailUrl,
    onPlayPause,
    playing,
    onTimeSliderChangeCommitted,
    onTimeSliderChange,
    timeSliderValue,
    onVolumeChange,
    volume,
    onMute,
    isMuted,
    currentTime,
    totalDuration,
    disabledControls,
}) => (
    <StyledAudioContainer thumbnailUrl={thumbnailUrl}>
        <StyledControlsOverlay />
        <StyledAudioControlsContainer>
            <StyledPlayPauseBtnContainer onClick={onPlayPause} data-target-id="custom-audio-play-pause-button">
                {!playing ? <PlayIcon width="1.5rem" height="1.5rem" /> : <PauseIcon width="1.5rem" height="1.5rem" />}
            </StyledPlayPauseBtnContainer>
            <StyledTimelineContainer>
                <MuiSlider
                    onChangeCommitted={(_, value) =>
                        !disabledControls ? onTimeSliderChangeCommitted(value as number) : undefined
                    }
                    onChange={(_, value) => (!disabledControls ? onTimeSliderChange(value as number) : undefined)}
                    value={timeSliderValue}
                    max={timeSliderSteps}
                    data-target-id="custom-audio-time-slider"
                />
            </StyledTimelineContainer>
            <StyledVolumeContainer>
                <MuiSlider
                    onChange={(event, value) => (!disabledControls ? onVolumeChange(value as number) : undefined)}
                    value={volume}
                    orientation="vertical"
                    max={volumeSliderSteps}
                    data-target-id="custom-audio-volume-slider"
                />
                <button
                    type="button"
                    onClick={!disabledControls ? onMute : undefined}
                    data-target-id="custom-audio-mute-button"
                >
                    {volume === 0 || isMuted ? (
                        <VolumeOffIcon width="1.5rem" height="1.5rem" />
                    ) : (
                        <VolumeOnIcon width="1.5rem" height="1.5rem" />
                    )}
                </button>
            </StyledVolumeContainer>
            <StyledCurrentTimeContainer>
                {currentTime === 0 ? formatSeconds(totalDuration) : formatSeconds(Math.round(currentTime))}
            </StyledCurrentTimeContainer>
        </StyledAudioControlsContainer>
    </StyledAudioContainer>
);

export default AudioPlayer;
